/** Private repo dashboard helpers — product copy and submit/error mapping (no env flags). */

export const PRIVATE_REPO_UI = {
  connected: {
    title: "GitHub connected",
    body: "RenderRepo can see the repositories you selected during installation.",
    previewNoteDisabled: "Private repo analysis is coming soon.",
    previewNoteEnabled:
      "Choose a private repository below to analyze it with RenderRepo.",
    manageButton: "Manage GitHub repositories",
    manageHint: "Choose or add repositories from GitHub.",
    refreshButton: "Refresh repositories",
    refreshLoading: "Refreshing repositories…",
    refreshSuccess: "Repositories refreshed.",
    refreshError: "Could not refresh repositories. Try again.",
    accessNote:
      "Repository access is controlled by your GitHub App installation.",
    reposEmpty:
      "No repositories selected yet. Manage GitHub repositories to choose repos for RenderRepo.",
    installReposError:
      "RenderRepo no longer has access to repositories on this GitHub account. Manage your GitHub connection and try again.",
  },
  analyze: {
    button: "Preview cost",
    loading: "Getting cost preview…",
    processing: "Processing…",
    success:
      "Analysis started. This repository will appear in Your Repositories.",
    disabled: "Private repo analysis is not available yet.",
    accessLost:
      "RenderRepo no longer has access to this repository. Manage your GitHub connection and try again.",
    alreadyAnalyzed: "Already analyzed",
    processingStatus: "Processing",
    failedStatus: "Failed",
    publicStatus: "Public repository",
    publicHint: "Use New Repository to analyze public GitHub repos.",
    inaccessibleStatus:
      "Analysis exists but is not available in this workspace",
    open: "Open",
    retry: "Retry analysis",
  },
};

const ACTIVE_REPO_STATUSES = new Set(["pending", "processing", "queued"]);
const FAILED_REPO_STATUSES = new Set(["failed", "error"]);

/** Row presentation for a GitHub-selected private repo in the connected section. */
export function getGitHubPrivateRepoRowState({
  existingRepo,
  isPrivate,
  privateAnalyzeEnabled,
  analyzeLoading = false,
}) {
  if (existingRepo) {
    const status = String(existingRepo.status || "").toLowerCase();
    if (status === "completed") {
      return {
        kind: "already_analyzed",
        statusLabel: PRIVATE_REPO_UI.analyze.alreadyAnalyzed,
        actionLabel: PRIVATE_REPO_UI.analyze.open,
        actionDisabled: false,
      };
    }
    if (ACTIVE_REPO_STATUSES.has(status)) {
      return {
        kind: "processing",
        statusLabel: PRIVATE_REPO_UI.analyze.processingStatus,
        actionLabel: PRIVATE_REPO_UI.analyze.processing,
        actionDisabled: true,
      };
    }
    if (FAILED_REPO_STATUSES.has(status)) {
      return {
        kind: "failed",
        statusLabel: PRIVATE_REPO_UI.analyze.failedStatus,
        actionLabel: PRIVATE_REPO_UI.analyze.retry,
        actionDisabled: analyzeLoading,
      };
    }
    return {
      kind: "processing",
      statusLabel: PRIVATE_REPO_UI.analyze.processingStatus,
      actionLabel: PRIVATE_REPO_UI.analyze.processing,
      actionDisabled: true,
    };
  }

  if (!isPrivate) {
    return {
      kind: "public_repo",
      statusLabel: PRIVATE_REPO_UI.analyze.publicStatus,
      hint: PRIVATE_REPO_UI.analyze.publicHint,
      actionDisabled: true,
    };
  }

  if (!privateAnalyzeEnabled) {
    return { kind: "read_only" };
  }

  return {
    kind: "analyze",
    actionLabel: analyzeLoading
      ? PRIVATE_REPO_UI.analyze.loading
      : PRIVATE_REPO_UI.analyze.button,
    actionDisabled: analyzeLoading,
  };
}

const ACCESS_DENIED_CODES = new Set([
  "INSTALLATION_REVOKED",
  "INSTALLATION_SUSPENDED",
  "REPO_NOT_IN_INSTALLATION",
  "ORG_ACCESS_DENIED",
  "INSTALLATION_NOT_FOUND",
  "INSTALLATION_REQUIRED",
]);

const DISABLED_CODES = new Set([
  "INGEST_DISABLED",
  "INGEST_NOT_READY",
  "PRIVATE_REPO_NOT_SUPPORTED",
]);

export function canShowPrivateAnalyzeButton(capabilities) {
  return (
    capabilities?.githubAppEnabled === true &&
    capabilities?.privateRepoIngestEnabled === true
  );
}

export function buildPrivateRepoSubmitPayload({
  owner,
  name,
  githubInstallationId,
  maxFileSize,
  llmModel,
  branch,
  ref,
  commitSha,
  quoteToken,
}) {
  return {
    owner,
    repo: name,
    githubInstallationId,
    ...(branch ? { branch } : {}),
    ...(ref ? { ref } : {}),
    ...(commitSha ? { commitSha } : {}),
    config: {
      ...(typeof maxFileSize === "number" ? { maxFileSize } : {}),
      ...(llmModel ? { llmModel } : {}),
    },
    ...(quoteToken ? { quoteToken } : {}),
    confirmed: true,
  };
}

export function buildPrivateRepoEstimatePayload(input) {
  const { confirmed: _confirmed, quoteToken: _quoteToken, ...payload } =
    buildPrivateRepoSubmitPayload(input);
  return payload;
}

export function findExistingUserRepo(userRepos, owner, name) {
  if (!owner || !name || !Array.isArray(userRepos)) return null;
  const ownerKey = owner.trim().toLowerCase();
  const nameKey = name.trim().toLowerCase();
  return (
    userRepos.find(
      (r) =>
        r.owner?.trim?.().toLowerCase() === ownerKey &&
        r.name?.trim?.().toLowerCase() === nameKey
    ) ?? null
  );
}

export function mapPrivateRepoSubmitError(responseData) {
  const code = responseData?.code;
  if (code && DISABLED_CODES.has(code)) {
    return PRIVATE_REPO_UI.analyze.disabled;
  }
  if (code && ACCESS_DENIED_CODES.has(code)) {
    return PRIVATE_REPO_UI.analyze.accessLost;
  }
  const message =
    typeof responseData?.error === "string" ? responseData.error : "";
  if (/PRIVATE_REPO|INGEST|_ENABLED|GITHUB_APP|server flag/i.test(message)) {
    return PRIVATE_REPO_UI.analyze.disabled;
  }
  if (message) return message;
  return "Could not start analysis. Please try again.";
}

export function privateRepoAnalyzeKey(installationId, fullName) {
  return `${installationId}:${fullName}`;
}

/** Pick the manage URL for the connected card (first installation with a URL). */
export function pickGitHubManageUrl(installations) {
  if (!Array.isArray(installations)) return null;
  const withUrl = installations.find(
    (inst) => typeof inst?.manageUrl === "string" && inst.manageUrl.length > 0
  );
  return withUrl?.manageUrl ?? null;
}

/** Summarize per-installation repo fetch results from refresh/list flows. */
export function summarizeInstallationRepoFetch(results) {
  if (!results || typeof results !== "object") {
    return { hadErrors: false, hadSuccess: false };
  }
  const entries = Object.values(results);
  return {
    hadErrors: entries.some((entry) => entry?.error),
    hadSuccess: entries.some(
      (entry) => !entry?.error && Array.isArray(entry?.repos)
    ),
  };
}

/** True when rendered dashboard copy must not leak server internals. */
export function dashboardCopyIsUserSafe(text) {
  if (!text || typeof text !== "string") return true;
  return !/installation\s*id|PRIVATE_REPO_INGEST_ENABLED|SSM|server flag/i.test(
    text
  );
}
